import Bugsnag from "@bugsnag/js";
import React, { FunctionComponent, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CartAddressForm from "@Components/molecules/cart/CartAddressForm";
import CartMondialRelay from "@Components/molecules/cart/CartMondialRelay";
import CheckoutPostcodeModal from "@Components/molecules/cart/CheckoutPostcodeModal";
import CheckoutShippingBottomSheet from "@Components/molecules/cart/CheckoutShippingBottomSheet";
import CheckoutShippingStep from "@Components/molecules/cart/CheckoutShippingStep";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import useCartContext from "@Hooks/useCartContext";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import {
  AutocompletePlace,
  BillingAddress,
  DeliveryAddress,
  MondialRelayLocation,
} from "@Types/addresses";
import CheerzApiAddresses from "@Types/api/addresses";
import CheerzApiCart from "@Types/api/cart";
import { ApiErrorResponse, ApiV2ErrorResponse } from "@Types/api/error";
import { Country, Step } from "@Types/cart";
import { FormError } from "@Types/error";
import { ShippingMethodName, ShippingModes } from "@Types/shipping";
import UserContent from "@Types/user";
import {
  convertAddressesFromApiToContent,
  convertAddressesFromContentToApi,
  convertMondialRelayLocationsFromApiToContent,
  isPickupSelected,
} from "@Utils/cart/addresses";
import { convertCartFromApiToContent, convertCartFromContentToApi } from "@Utils/cart/cart";
import { convertToShippingMethod } from "@Utils/cart/deliveryMethods";
import { getShippingMethods } from "@Utils/cart/shipping";
import { errorManager } from "@Utils/error";
import { convertToCheckoutStepProperties, setDataLayerTrack } from "@Utils/track";
import "./style.scss";

interface Props {
  countries: Country[];
  disabled: boolean;
  onChangeShippingMethod: (method: ShippingMethodName) => void;
  onConfirmZipCodeModal: () => void;
  onDismissZipCodeModal: () => void;
  onSelectAddressForPickup: (place: AutocompletePlace | undefined) => void;
  onSubmitAddress: ({
    billing,
    delivery,
  }: {
    billing: Partial<BillingAddress> | null;
    delivery: Partial<DeliveryAddress>;
  }) => void;
  pickupLocations: MondialRelayLocation[];
  postcode: string | undefined;
  shippingErrors: FormError[];
  shippingOptions: { home: ShippingModes; mondialRelay: ShippingModes };
  showZipCodeModal: boolean;
  user: UserContent;
  waitingPickupList: boolean;
}

interface ContainerProps {
  disabled: boolean;
  onChangeStep: (step: Step) => void;
  onValidatePostcode: (
    postcode: string | undefined,
    countryTag: string | undefined
  ) => Promise<boolean>;
}

const CartAddressStep: FunctionComponent<Props> = ({
  countries,
  disabled,
  onChangeShippingMethod,
  onConfirmZipCodeModal,
  onDismissZipCodeModal,
  onSelectAddressForPickup,
  onSubmitAddress,
  pickupLocations,
  postcode,
  shippingErrors,
  shippingOptions,
  showZipCodeModal,
  user,
  waitingPickupList,
}) => {
  const [selectedPickupLocation, setSelectedPickupLocation] = useState<
    MondialRelayLocation | undefined
  >();
  const [displayShippingBottomSheet, setDisplayShippingBottomSheet] = useState<boolean>(false);
  const { isSmallScreen } = useBreakpoint();
  const intl = useIntl();
  const localeSlug = intl.locale;
  const callbackUrl = encodeURIComponent(`/${localeSlug}/cart`);
  const loginUrl = `/${localeSlug}/users/sign_in?callback_url=${callbackUrl}`;
  const shippingMethods = getShippingMethods(shippingOptions);

  const handleChangeShippingMethod = (method: ShippingMethodName) => {
    onChangeShippingMethod(method);
    isSmallScreen && isPickupSelected(method) && setDisplayShippingBottomSheet(true);
  };

  const handleDismissBottomSheet = () => {
    setDisplayShippingBottomSheet(false);
  };

  const handleSelectAddressForPickup = (place: AutocompletePlace | undefined) => {
    setSelectedPickupLocation(undefined);
    onSelectAddressForPickup(place);
  };

  const handleSelectPickupLocation = (location?: MondialRelayLocation) => {
    setSelectedPickupLocation(location);
  };

  return (
    <div className="CartAddressStep">
      {user.isGuest && (
        <a className="CartAddressStep-login" href={loginUrl}>
          <FormattedMessage id={"cheerz_CartAddressStep_login"} />
        </a>
      )}
      <CheckoutShippingStep
        currency={user.currency}
        disabled={disabled}
        onChangeShippingMethod={handleChangeShippingMethod}
        onSelectAddressForPickup={handleSelectAddressForPickup}
        onSelectPickupLocation={handleSelectPickupLocation}
        pickupLocations={pickupLocations}
        selectedPickupLocation={selectedPickupLocation}
        shippingErrors={shippingErrors}
        shippingMethods={shippingMethods}
        waitingPickupList={waitingPickupList}
      />
      {(shippingOptions.home.normal.selected || shippingOptions.home.express.selected) && (
        <CartAddressForm countries={countries} disabled={disabled} onSubmit={onSubmitAddress} />
      )}
      <CheckoutPostcodeModal
        display={showZipCodeModal}
        onConfirm={onConfirmZipCodeModal}
        onDismiss={onDismissZipCodeModal}
        postcode={postcode ? postcode : ""}
      />

      {shippingOptions.mondialRelay &&
        (shippingOptions.mondialRelay.normal.selected ||
          shippingOptions.mondialRelay.express.selected) && (
          <CartMondialRelay
            disabled={disabled}
            mondialRelaySelectedLocation={selectedPickupLocation}
            onSelectLocation={handleSelectPickupLocation}
            onSubmitAddress={onSubmitAddress}
          />
        )}

      {isSmallScreen && (
        <CheckoutShippingBottomSheet
          disabled={disabled}
          display={displayShippingBottomSheet}
          onDismiss={handleDismissBottomSheet}
          onSelectAddressForPickup={handleSelectAddressForPickup}
          onSelectPickupLocation={handleSelectPickupLocation}
          pickupLocations={pickupLocations}
          shippingErrors={shippingErrors}
          waitingPickupList={waitingPickupList}
        />
      )}
    </div>
  );
};

export { CartAddressStep };

const CartAddressStepContainer: FunctionComponent<ContainerProps> = ({
  disabled,
  onChangeStep,
  onValidatePostcode,
}) => {
  const intl = useIntl();
  const { addresses, cart, configuration, dispatch, user } = useCartContext();
  const { dispatchNotification } = useNotificationsContext();
  const [postcodeWarningAddresses, setPostcodeWarningAddresses] = useState<{
    billing: Partial<BillingAddress> | null;
    delivery: Partial<DeliveryAddress>;
  }>(addresses);
  const [showZipCodeModal, setShowZipCodeModal] = useState(false);
  const [pickupLocations, setPickupLocations] = useState<MondialRelayLocation[]>([]);
  const [shippingErrors, setShippingErrors] = useState<FormError[]>([]);
  const [waitingPickupList, setWaitingPickupList] = useState(false);

  useEffect(() => {
    setDataLayerTrack("Checkout Step Viewed", convertToCheckoutStepProperties("Address", 2, cart));
  }, []);

  const handleSelectAddressForPickup = (place: AutocompletePlace | undefined) => {
    setShippingErrors([]);
    if (place !== undefined) {
      if (
        (!!place.position.lat && !!place.position.lng) ||
        (!!place.postCode && !!place.countryTag)
      ) {
        setWaitingPickupList(true);
        configuration.apiProviders.konnektor
          .getMondialRelayLocations(place)
          .then(({ response }) => {
            setPickupLocations(convertMondialRelayLocationsFromApiToContent(response));
            setWaitingPickupList(false);
          })
          .catch((error: ApiV2ErrorResponse) => {
            setWaitingPickupList(false);
            // TODO: put this inside apiV2ErrorManager after notification system refacto
            setShippingErrors([
              {
                input: "pickUpAddress",
                message: intl.formatMessage({
                  id:
                    error.response?.data?.error?.error_code === "KOBE-002" // KOBE-002 (Konnektor): "Not found."
                      ? "cheerz_CartAddressStep_pickup_notFound"
                      : "cheerz_CartAddressStep_pickup_error",
                }),
              },
            ]);
            // TODO: use apiV2ErrorManager after tech/platform-days merged
            Bugsnag.notify(
              Error(
                error.response?.data?.error?.developer_message
                  ? error.response.data.error.developer_message
                  : intl.formatMessage({
                      id: "cheerz_Notification_CFE-001",
                    })
              )
            );
          });
      } else {
        setPickupLocations([]);
        setShippingErrors([
          {
            input: "pickUpAddress",
            message: intl.formatMessage({
              id: "cheerz_CartAddressStep_pickup_notFound",
            }),
          },
        ]);
      }
    } else {
      setPickupLocations([]);
    }
  };

  const handleSubmitAddress = (
    {
      billing,
      delivery,
    }: {
      billing: Partial<BillingAddress> | null;
      delivery: Partial<DeliveryAddress>;
    },
    confirmed = false
  ) => {
    setPostcodeWarningAddresses({
      billing,
      delivery,
    });
    onValidatePostcode(delivery.postcode, delivery.countryTag)
      .then((isValidPostcode: boolean) => {
        if (isValidPostcode === false && confirmed === false) {
          setShowZipCodeModal(true);
          return;
        }
        if (configuration) {
          dispatch({
            disabled: true,
            type: "setDisabledCart",
          });

          configuration.apiProviders.cheerz
            .saveAddresses(convertAddressesFromContentToApi(billing, delivery))
            .then((addressesResponse: CheerzApiAddresses) => {
              dispatch({
                addresses: {
                  ...convertAddressesFromApiToContent(addressesResponse),
                },
                type: "setSavedAddresses",
              });

              const updatedCartJson = convertCartFromContentToApi(cart);

              configuration.apiProviders.cheerz
                .saveCart(updatedCartJson)
                .then((cartResponse: CheerzApiCart) => {
                  dispatch({
                    cart: {
                      ...convertCartFromApiToContent(cartResponse),
                    },
                    type: "setSavedCart",
                  });
                  onChangeStep("overview");
                })
                .catch((error: ApiErrorResponse) => {
                  dispatch({
                    disabled: false,
                    type: "setDisabledCart",
                  });
                  Bugsnag.notify(
                    Error(
                      error.response?.data?.error_description[0]
                        ? `${error.response?.data?.error_description[0]}`
                        : "Error on saveCart call"
                    )
                  );
                });
            })
            .catch((error: ApiErrorResponse) => {
              const errorDescription = error.response?.data?.error_description
                ? error.response?.data?.error_description[0]
                : "";
              dispatch({
                disabled: false,
                type: "setDisabledCart",
              });
              setDataLayerTrack("Address Error Occured", {
                error: errorDescription,
              });
              errorManager(dispatchNotification, "CFE-004", errorDescription);
            });
        }
      })
      .catch((error: ApiErrorResponse) => {
        Bugsnag.notify(
          Error(
            error.response?.data?.error_description[0]
              ? `${error.response?.data?.error_description[0]}`
              : "Error on checkPostCode call"
          )
        );
      });
  };

  const handleConfirmZipCodeModal = () => {
    setDataLayerTrack("Wrong Zip Code Modal Ignore Clicked");
    setShowZipCodeModal(false);
    handleSubmitAddress(postcodeWarningAddresses, true);
  };
  const handleDismissZipCodeModal = () => {
    setDataLayerTrack("Wrong Zip Code Modal Correct Clicked");
    setShowZipCodeModal(false);
  };

  const handleChangeShippingMethod = (method: ShippingMethodName) => {
    dispatch({
      method,
      type: "setShippingOptionMethod",
    });
    setDataLayerTrack("Cart Shipping Method Clicked", {
      shippingMethod: convertToShippingMethod(method),
    });
  };

  return (
    <CartAddressStep
      countries={[]}
      disabled={disabled}
      onChangeShippingMethod={handleChangeShippingMethod}
      onConfirmZipCodeModal={handleConfirmZipCodeModal}
      onDismissZipCodeModal={handleDismissZipCodeModal}
      onSelectAddressForPickup={handleSelectAddressForPickup}
      onSubmitAddress={handleSubmitAddress}
      pickupLocations={pickupLocations}
      postcode={addresses.delivery && addresses.delivery.postcode}
      shippingErrors={shippingErrors}
      shippingOptions={cart.shippingOptions}
      showZipCodeModal={showZipCodeModal}
      user={user}
      waitingPickupList={waitingPickupList}
    />
  );
};

export default CartAddressStepContainer;
